<template>
  <app-page labelledby="page-title">
    <page-header
      :title="familyMeta.title"
      :description="familyMeta.description"
    >
      <template #actions>
        <q-btn
          flat
          no-caps
          color="primary"
          icon="refresh"
          label="Обновить"
          :loading="savedReportsLoading"
          @click="loadSavedReports"
        />
      </template>
    </page-header>

    <page-section
      title="Доступные отчеты"
      :description="`${catalogReports.length} ${reportCountLabel}`"
      data-testid="report-catalog"
    >
      <div class="report-catalog">
        <section
          v-for="group in groupedReports"
          :key="group.group"
          class="report-catalog__group"
          :aria-labelledby="`report-group-${slug(group.group)}`"
        >
          <h3 :id="`report-group-${slug(group.group)}`" class="report-catalog__group-title">
            {{ group.group }}
          </h3>
          <q-list bordered separator class="report-catalog__list">
            <q-item
              v-for="report in group.reports"
              :key="report.code"
              class="report-catalog__item"
              :data-testid="`report-catalog-item-${report.code}`"
            >
              <q-item-section avatar>
                <q-icon :name="reportIcon(report)" color="primary" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-weight-medium">{{ report.name }}</q-item-label>
                <q-item-label caption>{{ report.description }}</q-item-label>
                <div class="report-catalog__meta">
                  <span>Готов к формированию</span>
                  <span v-if="report.exportFormats.length">
                    Экспорт: {{ report.exportFormats.join(', ').toUpperCase() }}
                  </span>
                </div>
              </q-item-section>
              <q-item-section side class="report-catalog__action">
                <q-btn
                  outline
                  no-caps
                  color="primary"
                  icon-right="arrow_forward"
                  label="Сформировать"
                  :aria-label="`Сформировать: ${report.name}`"
                  :to="{ name: report.routeName }"
                />
              </q-item-section>
            </q-item>
          </q-list>
        </section>
      </div>
    </page-section>

    <page-section
      title="Сохраненные отчеты"
      description="Документы, ранее созданные через формы бухгалтерской отчетности."
      data-testid="saved-reports-section"
    >
      <app-data-table
        title="Список отчетов"
        :count="filteredSavedReports.length"
        :rows="filteredSavedReports"
        :columns="tableColumns"
        :pagination="tablePagination"
        :state="savedReportsTableState"
        :state-title="savedReportsStateTitle"
        :state-message="savedReportsStateMessage"
        storage-key="reports-saved"
        data-testid="saved-reports-table"
        @row-click="onSavedReportClick"
        @retry="loadSavedReports"
        @reset="resetSavedFilters"
      >
        <template #filters>
          <filter-bar
            v-model="savedSearch"
            search-label="Поиск по номеру или названию"
            search-test-id="saved-reports-search"
            :has-filters="hasSavedFilters"
            :applied-filters="appliedSavedFilters"
            @reset="resetSavedFilters"
            @remove="removeSavedFilter"
          >
            <template #quick>
              <saved-report-filters
                v-model:report-type="reportTypeFilter"
                v-model:period-from="periodRange.from"
                v-model:period-to="periodRange.to"
                :report-type-options="reportTypeOptions"
                :date-input-mask="dateInputMask"
                :quasar-date-mask="quasarDateMask"
                :date-rules="dateRules"
                @change="onPeriodChange"
              />
            </template>
            <template #advanced>
              <saved-report-filters
                v-model:report-type="reportTypeFilter"
                v-model:period-from="periodRange.from"
                v-model:period-to="periodRange.to"
                :report-type-options="reportTypeOptions"
                :date-input-mask="dateInputMask"
                :quasar-date-mask="quasarDateMask"
                :date-rules="dateRules"
                @change="onPeriodChange"
              />
            </template>
          </filter-bar>
        </template>
        <template #body-cell-status="props">
          <q-td :props="props">
            <q-badge :color="savedReportRoute(props.row) ? 'positive' : 'grey-7'" outline>
              {{ savedReportRoute(props.row) ? 'Доступен' : 'Только архив' }}
            </q-badge>
          </q-td>
        </template>
        <template #body-cell-actions="props">
          <q-td :props="props">
            <q-btn
              v-if="savedReportRoute(props.row)"
              flat
              dense
              no-caps
              color="primary"
              icon="open_in_new"
              label="Открыть"
              :aria-label="`Открыть отчет ${props.row.name || props.row.in_org_number || props.row.id}`"
              @click.stop="openSavedReport(props.row)"
            />
            <span v-else class="text-caption text-grey-7">
              Просмотр недоступен
              <q-tooltip>Для этого архивного типа еще нет страницы просмотра.</q-tooltip>
            </span>
          </q-td>
        </template>
      </app-data-table>
    </page-section>
  </app-page>
</template>

<script>
import moment from 'moment'
import AppDataTable from '../../../components/ui/AppDataTable.vue'
import AppPage from '../../../components/ui/AppPage.vue'
import FilterBar from '../../../components/ui/FilterBar.vue'
import PageHeader from '../../../components/ui/PageHeader.vue'
import PageSection from '../../../components/ui/PageSection.vue'
import SavedReportFilters from './components/SavedReportFilters.vue'
import {
  getReportsByFamily,
  getReportsByGroup,
  REPORT_FAMILIES,
  REPORT_FAMILY_META,
  REPORTS
} from './utils/reportMetadata'
import {
  DATE_DISPLAY_FORMAT,
  DATE_INPUT_MASK,
  QUASAR_DATE_MASK,
  formatDate,
  normalizeDate as normalizeDisplayDate,
  parseDate,
  unixEndOfDay,
  unixStartOfDay
} from '../../../services/dateFormat'

const tableColumns = [
  { name: 'in_org_number', align: 'left', label: 'Номер', field: 'in_org_number', sortable: true, required: true },
  { name: 'name', align: 'left', label: 'Название', field: 'name', required: true },
  { name: 'user', align: 'left', label: 'Пользователь', field: 'user', sortable: true, classes: 'column-priority-secondary', headerClasses: 'column-priority-secondary' },
  { name: 'report_type', align: 'left', label: 'Тип отчета', field: 'report_type_display', sortable: true },
  { name: 'date', align: 'left', label: 'Дата', field: 'date', sortable: true, required: true },
  { name: 'status', align: 'left', label: 'Статус', field: 'status' },
  { name: 'actions', align: 'right', label: 'Действия', field: 'actions', required: true }
]

const infoRoutes = {
  balance: 'reports_info_balance',
  balance_sheet: 'reports_info_balance',
  fin_rez: 'reports_info_fin_rez',
  financial_results: 'reports_info_fin_rez',
  cap_change: 'reports_info_cap_change',
  capital_changes: 'reports_info_cap_change',
  money_move: 'reports_info_money_move',
  cash_flow: 'reports_info_money_move',
  money_using: 'reports_info_money_using',
  targeted_funds: 'reports_info_money_using'
}

export default {
  name: 'reports_list',
  components: {
    AppDataTable,
    AppPage,
    FilterBar,
    PageHeader,
    PageSection,
    SavedReportFilters
  },
  data() {
    const storedPeriod = this.$store.state.filter_period || {}
    return {
      savedReports: [],
      savedReportsLoading: false,
      savedReportsState: 'loading',
      savedReportsError: '',
      savedSearch: '',
      reportTypeFilter: { label: 'Все отчеты', value: 'any' },
      reportTypeOptions: [
        { label: 'Все отчеты', value: 'any' },
        ...REPORTS
          .filter((report) => report.kind === 'accounting')
          .map((report) => ({ label: report.name, value: report.code }))
      ],
      periodRange: {
        from: this.normalizeDate(storedPeriod.from || moment().startOf('quarter')),
        to: this.normalizeDate(storedPeriod.to || moment().endOf('day'))
      },
      dateInputMask: DATE_INPUT_MASK,
      quasarDateMask: QUASAR_DATE_MASK,
      dateRules: [
        (value) => parseDate(value).isValid() || 'Дата в формате ДД.ММ.ГГГГ'
      ],
      tableColumns,
      tablePagination: {
        sortBy: 'date',
        descending: true,
        page: 1,
        rowsPerPage: 20
      }
    }
  },
  computed: {
    reportFamily() {
      return this.$route.meta.reportFamily || REPORT_FAMILIES.ALL
    },
    familyMeta() {
      return REPORT_FAMILY_META[this.reportFamily] || REPORT_FAMILY_META[REPORT_FAMILIES.ALL]
    },
    groupedReports() {
      return getReportsByGroup(this.reportFamily)
    },
    catalogReports() {
      return getReportsByFamily(this.reportFamily)
    },
    reportCountLabel() {
      const count = this.catalogReports.length
      if (count % 10 === 1 && count % 100 !== 11) return 'форма'
      if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return 'формы'
      return 'форм'
    },
    filteredSavedReports() {
      const needle = String(this.savedSearch || '').trim().toLowerCase()
      if (!needle) return this.savedReports
      return this.savedReports.filter((row) => {
        return [
          row.in_org_number,
          row.name,
          row.user,
          row.report_type_display
        ].some((value) => String(value || '').toLowerCase().includes(needle))
      })
    },
    savedPeriodError() {
      const start = parseDate(this.periodRange.from)
      const end = parseDate(this.periodRange.to)
      if (!start.isValid() || !end.isValid()) return 'Укажите даты в формате ДД.ММ.ГГГГ.'
      if (start.isAfter(end)) return 'Дата начала не может быть позже даты окончания.'
      return ''
    },
    savedReportsTableState() {
      if (this.savedReportsLoading || this.savedReportsState === 'loading') return 'loading'
      if (this.savedReportsState === 'access-denied') return 'access-denied'
      if (this.savedReportsState === 'error') return 'error'
      if (!this.filteredSavedReports.length) {
        return this.hasSavedFilters ? 'filtered-empty' : 'empty'
      }
      return 'ready'
    },
    savedReportsStateTitle() {
      if (this.savedReportsState === 'error' && this.savedPeriodError) return 'Проверьте период'
      if (this.savedReportsTableState === 'empty') return 'Сохраненных отчетов пока нет'
      return ''
    },
    savedReportsStateMessage() {
      if (this.savedReportsState === 'error') return this.savedReportsError
      if (this.savedReportsTableState === 'empty') {
        return 'Сформированные документы появятся здесь после сохранения.'
      }
      return ''
    },
    hasSavedFilters() {
      return Boolean(
        this.savedSearch ||
        this.reportTypeFilter.value !== 'any' ||
        this.periodRange.from !== moment().startOf('quarter').format(DATE_DISPLAY_FORMAT) ||
        this.periodRange.to !== moment().endOf('day').format(DATE_DISPLAY_FORMAT)
      )
    },
    appliedSavedFilters() {
      const applied = []
      if (this.reportTypeFilter.value !== 'any') {
        applied.push({ key: 'type', label: this.reportTypeFilter.label })
      }
      applied.push({ key: 'period', label: `${this.periodRange.from} — ${this.periodRange.to}` })
      return applied
    }
  },
  watch: {
    '$route.meta.reportFamily'() {
      this.savedSearch = ''
    }
  },
  mounted() {
    this.emitter.on('didReceiveDocumentsList', this.onSavedReportsReceived)
    this.emitter.on('accessDeniedErrorReceived', this.onAccessDenied)
    this.loadSavedReports()
  },
  beforeUnmount() {
    this.emitter.off('didReceiveDocumentsList', this.onSavedReportsReceived)
    this.emitter.off('accessDeniedErrorReceived', this.onAccessDenied)
    this.emitter.off('connectionReady', this.loadSavedReports)
  },
  methods: {
    normalizeDate(value) {
      return normalizeDisplayDate(value)
    },
    slug(value) {
      return String(value).toLowerCase().replace(/[^a-z0-9а-яё]+/gi, '-')
    },
    reportIcon(report) {
      if (report.kind === 'vat') return 'percent'
      if (report.kind === 'book') return 'menu_book'
      if (report.family === REPORT_FAMILIES.SPECIAL) return 'fact_check'
      return 'description'
    },
    loadSavedReports() {
      if (this.savedPeriodError) {
        this.savedReportsLoading = false
        this.savedReportsState = 'error'
        this.savedReportsError = this.savedPeriodError
        return
      }
      this.savedReportsLoading = true
      this.savedReportsState = 'loading'
      this.savedReportsError = ''
      const send = () => {
        this.emitter.off('connectionReady', send)
        this.connection.getDocumentsList(
          'reports-list',
          unixStartOfDay(this.periodRange.from),
          unixEndOfDay(this.periodRange.to),
          'all',
          'report',
          true,
          this.reportTypeFilter.value
        )
      }
      if (this.connection.isAuthenticated) {
        send()
      } else {
        this.emitter.on('connectionReady', send)
      }
    },
    onSavedReportsReceived(data) {
      if (data.action !== 'get_document_list' || (data.request_id && data.request_id !== 'reports-list')) return
      this.savedReports = (data.data || []).map((item) => ({
        ...item,
        date: formatDate(item.date, item.date || '')
      }))
      this.savedReportsLoading = false
      this.savedReportsState = 'ready'
    },
    onAccessDenied(data) {
      if (data.action !== 'get_document_list' || (data.request_id && data.request_id !== 'reports-list')) return
      this.savedReportsLoading = false
      this.savedReportsState = 'access-denied'
    },
    onPeriodChange() {
      this.$store.commit('updateFilterPeriod', {
        from: this.periodRange.from,
        to: this.periodRange.to
      })
      this.loadSavedReports()
    },
    resetSavedFilters() {
      this.savedSearch = ''
      this.reportTypeFilter = this.reportTypeOptions[0]
      this.periodRange = {
        from: moment().startOf('quarter').format(DATE_DISPLAY_FORMAT),
        to: moment().endOf('day').format(DATE_DISPLAY_FORMAT)
      }
      this.onPeriodChange()
    },
    removeSavedFilter(key) {
      if (key === 'type') this.reportTypeFilter = this.reportTypeOptions[0]
      if (key === 'period') {
        this.periodRange = {
          from: moment().startOf('quarter').format(DATE_DISPLAY_FORMAT),
          to: moment().endOf('day').format(DATE_DISPLAY_FORMAT)
        }
      }
      this.onPeriodChange()
    },
    savedReportRoute(row) {
      const routeName = infoRoutes[row.report_type]
      if (!routeName) return null
      return { name: routeName, params: { id: row.id, history_id: 'last' } }
    },
    openSavedReport(row) {
      const route = this.savedReportRoute(row)
      if (route) this.$router.push(route)
    },
    onSavedReportClick(event, row) {
      this.openSavedReport(row)
    }
  }
}
</script>

<style scoped>
.report-catalog {
  display: grid;
  gap: var(--sid-space-5);
}

.report-catalog__group-title {
  margin: 0 0 var(--sid-space-2);
  color: var(--sid-color-text-secondary);
  font-size: var(--sid-font-size-label);
  font-weight: var(--sid-font-weight-semibold);
  letter-spacing: 0.04em;
  line-height: var(--sid-line-height-label);
  text-transform: uppercase;
}

.report-catalog__list {
  border-color: var(--sid-color-line);
  border-radius: var(--sid-radius-large);
  background: var(--sid-color-surface-0);
}

.report-catalog__item {
  min-height: 5rem;
  padding: var(--sid-space-3) var(--sid-space-4);
}

.report-catalog__meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sid-space-2) var(--sid-space-4);
  margin-top: var(--sid-space-2);
  color: var(--sid-color-text-secondary);
  font-size: var(--sid-font-size-label);
}

@media (max-width: 599px) {
  .report-catalog__item {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .report-catalog__item :deep(.q-item__section--avatar) {
    min-width: 2.25rem;
  }

  .report-catalog__action {
    width: 100%;
    padding: var(--sid-space-2) 0 0 2.25rem;
    align-items: stretch;
  }

  .report-catalog__action :deep(.q-btn) {
    width: 100%;
    min-height: var(--sid-touch-target);
  }
}
</style>
